/**
 * React Query hooks for business hours management
 * Provides data fetching, caching, and mutation capabilities for business hours settings
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { BusinessHours, BusinessHoursForm } from '../../services/business/types';
import type { Json } from '@/integrations/supabase/types';

// Query keys for business hours
export const businessHoursQueryKeys = {
  all: ['businessHours'] as const,
  details: () => [...businessHoursQueryKeys.all, 'details'] as const,
} as const;

/**
 * Hook to fetch business hours
 */
export const useBusinessHours = () => {
  return useQuery({
    queryKey: businessHoursQueryKeys.details(),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_business_hours');
      
      if (error) {
        throw new Error(error.message);
      }
      
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      // Don't retry on permission errors
      if (error.message.includes('insufficient_permissions')) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

/**
 * Hook to update business hours
 */
export const useUpdateBusinessHours = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: BusinessHoursForm) => {
      const { data, error } = await supabase.rpc('update_business_hours', {
        p_timezone: formData.timezone,
        p_business_hours: formData.businessHours as unknown as Json,
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      return data;
    },
    onSuccess: () => {
      // Invalidate and refetch to prevent cache race conditions
      queryClient.invalidateQueries({ queryKey: businessHoursQueryKeys.details() });
      
      toast.success('Business hours updated successfully');
    },
    onError: (error: Error) => {
      console.error('Error updating business hours:', error);
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to update business hours';
      
      if (error.message.includes('insufficient_permissions')) {
        errorMessage = 'You do not have permission to update business hours';
      } else if (error.message.includes('invalid_timezone')) {
        errorMessage = 'Invalid timezone format';
      } else if (error.message.includes('invalid_business_hours')) {
        errorMessage = 'Invalid business hours format';
      } else if (error.message.includes('invalid_time_format')) {
        errorMessage = 'Invalid time format. Please use HH:MM format';
      } else if (error.message.includes('end_before_start')) {
        errorMessage = 'End time must be after start time';
      }
      
      toast.error(errorMessage);
    },
  });
};

/**
 * Hook to prefetch business hours
 * Useful for preloading data before navigation
 */
export const usePrefetchBusinessHours = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.prefetchQuery({
      queryKey: businessHoursQueryKeys.details(),
      queryFn: async () => {
        const { data, error } = await supabase.rpc('get_business_hours');
        
        if (error) {
          throw new Error(error.message);
        }
        
        return data;
      },
      staleTime: 5 * 60 * 1000,
    });
  };
};

/**
 * Hook to get cached business hours without triggering a fetch
 */
export const useCachedBusinessHours = (): { timezone: string; business_hours: BusinessHours } | undefined => {
  const queryClient = useQueryClient();
  return queryClient.getQueryData(businessHoursQueryKeys.details());
};

/**
 * Hook to manually invalidate business hours cache
 */
export const useInvalidateBusinessHours = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: businessHoursQueryKeys.all });
  };
};
